import { debounce } from 'lodash';
import { initNumberField } from '@general/helpers';
import { changeCart } from '@services/cart';
import { closeModal, openModal } from './modals';
import breakpoints from 'breakpoints';
import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';

/**
 * Finds the minicart element within the specified root element.
 *
 * @param {Document|HTMLElement} [root=document] - The root element to search within.
 * @returns {HTMLElement|null} The minicart element if found, otherwise null.
 */
const findMinicart = (root = document) => root.querySelector('#MinicartDrawer');

/**
 * Initializes the minicart by setting up event listeners and initializing line items.
 */
const initMinicart = () => {
  const minicart = findMinicart();
  if (!minicart) return;
  const privacyCheckbox = minicart.querySelector(
    '.privacy-checkbox__container',
  );
  if (
    privacyCheckbox &&
    typeof window.dAgency.initPrivacyCheckbox == 'function'
  )
    window.dAgency.initPrivacyCheckbox(privacyCheckbox);
  minicart.querySelectorAll('.cart-line__wrapper[data-key]').forEach((line) =>
    initLine({
      line,
      beforeFetch: () => {
        minicart.classList.add('loading');
      },
      afterFetch: () => {
        updateMinicart();
      },
    }),
  );
  if (typeof window?.dAgency?.initProductTile === 'function')
    minicart
      .querySelectorAll('.product-tile__container')
      .forEach((tile) => window.dAgency.initProductTile(tile));
  if (typeof window?.dAgency?.initFitSize === 'function')
    minicart
      .querySelectorAll('.fit-size__container')
      .forEach((fitSizeWrapper) => window.dAgency.initFitSize(fitSizeWrapper));
  const carousel = minicart.querySelector('.minicart__upsell');
  if (carousel && carousel.querySelectorAll('.swiper-slide').length > 1) {
    const prevEl = carousel.querySelector('.minicart__upsell-arrow--left');
    const nextEl = carousel.querySelector('.minicart__upsell-arrow--right');
    const options = {
      loop: false,
      modules: prevEl && nextEl ? [Navigation] : [],
      ...(prevEl && nextEl
        ? {
            navigation: {
              prevEl,
              nextEl,
            },
          }
        : {}),
      slidesPerView: 'auto',
      breakpoints: {
        [parseInt(breakpoints.sm)]: {
          spaceBetween: 8,
        },
        [parseInt(breakpoints.md)]: {
          spaceBetween: 12,
        },
        [parseInt(breakpoints.lg)]: {
          spaceBetween: 16,
        },
        [parseInt(breakpoints.xl)]: {
          spaceBetween: 24,
        },
      },
    };
    new Swiper(carousel, options);
  }
};

/**
 * Updates the minicart content by fetching the latest data and reinitializing the minicart.
 */
const updateMinicart = async () => {
  const minicart = findMinicart();
  if (!minicart) return;
  minicart.classList.add('loading');
  await fetch(minicart.dataset.refreshUrl)
    .then((res) => res.text())
    .then((data) => {
      const minicartSection = new DOMParser().parseFromString(
        data,
        'text/html',
      );
      const fetchMinicart = findMinicart(minicartSection);
      if (fetchMinicart) minicart.innerHTML = fetchMinicart.innerHTML;
      minicart
        .querySelectorAll('.modal__backdrop , .modal__close')
        .forEach((closer) =>
          closer.addEventListener('click', () => closeModal(minicart)),
        );
      initMinicart();
    });
  minicart.classList.remove('loading');
};

/**
 * Initializes a line item in the minicart by setting up event listeners for
 * quantity changes and item removal.
 * @param {Object} options - Options for initializing the line item.
 * @param {HTMLElement} options.line - The line item element.
 * @param {Function} [options.beforeFetch] - Function to call before sending the request.
 * @param {Function} [options.afterFetch] - Function to call after successfully changing the cart item.
 */
const initLine = ({ line, beforeFetch, afterFetch }) => {
  if (!line) return;
  const minusBtn = line.querySelector('.cart-line__quantity-btn--minus');
  const plusBtn = line.querySelector('.cart-line__quantity-btn--plus');
  const field = line.querySelector('.cart-line__quantity-value');

  if (minusBtn && plusBtn && field) {
    initNumberField({
      plusBtn,
      minusBtn,
      field,
    });

    field.addEventListener(
      'change',
      debounce(() => {
        changeCart({
          key: line.dataset.key,
          quantity: parseInt(field.value),
          callbacks: {
            beforeFetch: beforeFetch,
            afterFetch: afterFetch,
          },
        });
      }, 300),
    );
  }

  const remove = line.querySelector('.cart-line__remove');
  if (remove) {
    remove.addEventListener('click', (e) => {
      e.preventDefault();
      changeCart({
        key: line.dataset.key,
        quantity: 0,
        callbacks: {
          beforeFetch: beforeFetch,
          afterFetch: afterFetch,
        },
      });
    });
  }
};

/**
 * Opens the minicart, optionally loading its content before opening.
 *
 * @param {boolean} [loadBeforeOpen=false] - Whether to load the minicart content before opening it.
 */
const openMinicart = async (loadBeforeOpen = false) => {
  if (loadBeforeOpen) await updateMinicart();
  openModal(findMinicart());
};

/**
 * Closes the minicart.
 */
const closeMinicart = () => closeModal(findMinicart());

export { initMinicart, openMinicart, closeMinicart, initLine };
